import { useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { CloseIcon } from "components/ui/icons/uploadbar/CloseIcon"
import { ConfirmModal } from "components/ui/modals/ConfirmModal/ConfirmModal"
import {
	useAutoConnectEcallElevation,
	useCreateEcallElevation,
	useDeleteEcallElevation,
	useUpdateEcall,
	useUpdateEcallElevations,
} from "hooks/api/quotation/quotations.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import AnnotationService from "services/annotations.service"
import {
	appendEcallElevationToCache,
	applyAutoConnectEcallElevationIntoCache,
	applyDisconnectedErefsToCache,
	incrementErefVersionInCache,
	removeEcallElevationFromCache,
	replaceEcallElevationInCache,
	spliceEcallIntoCache,
} from "helpers/quotationCache.helper"
import type { TEcall } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import { ElevationReferenceRow, type TElevationRowState } from "./ElevationReferenceRow/ElevationReferenceRow"
import style from "./EcallSidebar.module.scss"

const MAX_ELEVATIONS = 4

type RowDraft = {
	key: string
	elevationId: number | null
	elevationVersion: number
	elevationNumber: string
	savedElevationNumber: string
	referenceLabel: string
	savedReferenceLabel: string
	erefId: number | null
	errored: boolean
}

type Props = {
	ecall: TEcall
	onClose?: () => void
	onNavigateToEref?: (erefId: number) => void
}

type ConfirmAction = { kind: "remove" | "detach"; rowKey: string }

const buildInitialRows = (ecall: TEcall): RowDraft[] =>
	ecall.elevation_numbers.map((en, index) => ({
		key: `el-${en.id ?? index}`,
		elevationId: en.id ?? null,
		elevationVersion: en.version ?? 0,
		elevationNumber: en.elevation_number ?? "",
		savedElevationNumber: en.elevation_number ?? "",
		referenceLabel: en.reference_label ?? "",
		savedReferenceLabel: en.reference_label ?? "",
		erefId: en.eref_id,
		errored: false,
	}))

const PlusGlyph = ({ disabled }: { disabled: boolean }) => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M3.33301 8H12.6663M8 3.33333V12.6667"
			stroke={disabled ? "#BEC2D6" : "#888DA6"}
			strokeWidth="1.6"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
)

export const EcallSidebar = ({ ecall, onClose, onNavigateToEref }: Props) => {
	const [searchParams] = useSearchParams()
	const quotationId = Number(searchParams.get("quotationID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()

	const ecallVersionRef = useRef(ecall.version)
	ecallVersionRef.current = ecall.version

	const [rows, setRows] = useState<RowDraft[]>(() => buildInitialRows(ecall))
	const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
	const [pendingConnectRowKey, setPendingConnectRowKey] = useState<string | null>(null)
	const nextRowKeyRef = useRef(0)

	const [roomName, setRoomName] = useState(ecall.room_name ?? "")
	const savedRoomNameRef = useRef(ecall.room_name ?? "")
	const [pageName, setPageName] = useState(ecall.page_name ?? "")
	const savedPageNameRef = useRef(ecall.page_name ?? "")

	const bumpEcallVersion = (): number => {
		const next = ecallVersionRef.current + 1
		ecallVersionRef.current = next
		return next
	}

	const { mutate: updateEcall } = useUpdateEcall()
	const { mutate: createEcallElevation } = useCreateEcallElevation()
	const { mutate: deleteEcallElevation } = useDeleteEcallElevation()
	const { mutate: updateEcallElevations } = useUpdateEcallElevations()
	const { mutate: autoConnectEcallElevation } = useAutoConnectEcallElevation()

	const cacheKey = ["quotation", quotationId]

	const writeCache = (
		updater: (cached: AxiosResponse<TQuotationDetail> | undefined) => AxiosResponse<TQuotationDetail> | undefined,
	) => {
		queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(cacheKey, updater)
		// Cache changes don't repaint annotations on their own, so resync this ecall's
		// highlight color from the freshly written links after every mutation.
		syncEcallHighlight()
	}

	// Recompute this ecall's highlight from the latest cache: it turns yellow once a
	// linked eref has furniture and back to gray when none do.
	const syncEcallHighlight = () => {
		const cached = queryClient.getQueryData<AxiosResponse<TQuotationDetail> | undefined>(cacheKey)
		for (const page of cached?.data?.related_objects ?? []) {
			const found = page.ecalls.find((e) => e.id === ecall.id)
			if (found) {
				AnnotationService.syncEcallConnectivity(found)
				return
			}
		}
	}

	const handleApiError = (e: unknown) => {
		formatError(e)
		queryClient.invalidateQueries({ queryKey: cacheKey })
	}

	const lookupErefVersion = (erefId: number): number | undefined => {
		const cached = queryClient.getQueryData<AxiosResponse<TQuotationDetail> | undefined>(cacheKey)
		for (const page of cached?.data?.related_objects ?? []) {
			const eref = page.erefs.find((e) => e.id === erefId)
			if (eref) return eref.version
		}
		return undefined
	}

	const lookupErefPageName = (erefId: number): string | undefined => {
		const cached = queryClient.getQueryData<AxiosResponse<TQuotationDetail> | undefined>(cacheKey)
		for (const page of cached?.data?.related_objects ?? []) {
			const eref = page.erefs.find((e) => e.id === erefId)
			if (eref) return eref.page_name
		}
		return undefined
	}

	const addDisabled = rows.length >= MAX_ELEVATIONS || pageName.trim().length === 0

	const updateRow = (key: string, patch: Partial<RowDraft>) => {
		setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)))
	}

	const replaceRow = (key: string, next: RowDraft) => {
		setRows((prev) => prev.map((row) => (row.key === key ? next : row)))
	}

	const removeRowLocal = (key: string) => {
		setRows((prev) => prev.filter((row) => row.key !== key))
	}

	const commitEcallField = (
		field: "room_name" | "page_name",
		value: string,
		savedRef: React.MutableRefObject<string>,
	) => {
		if (value === savedRef.current) return
		updateEcall(
			{
				quotationId,
				ecallId: ecall.id,
				body: { version: ecallVersionRef.current, [field]: value },
			},
			{
				onSuccess: (response) => {
					const updated = response?.data
					if (!updated) return
					const { disconnected_erefs, ...updatedEcall } = updated
					ecallVersionRef.current = updatedEcall.version
					savedRef.current = field === "room_name" ? updatedEcall.room_name ?? "" : updatedEcall.page_name ?? ""
					if (field === "page_name") {
						setRows((prev) =>
							prev.map((row) => {
								if (row.elevationId === null) return row
								const matched = updatedEcall.elevation_numbers.find((en) => en.id === row.elevationId)
								if (!matched) return row
								return {
									...row,
									elevationVersion: matched.version ?? row.elevationVersion,
									erefId: matched.eref_id,
									errored: false,
								}
							}),
						)
					}
					writeCache((cached) => {
						const next = spliceEcallIntoCache(cached, updatedEcall)
						return applyDisconnectedErefsToCache(next, ecall.id, disconnected_erefs)
					})
				},
				onError: handleApiError,
			},
		)
	}

	const handlePlus = () => {
		if (addDisabled) return
		const draftKey = `draft-${nextRowKeyRef.current++}`
		setRows((prev) => [
			...prev,
			{
				key: draftKey,
				elevationId: null,
				elevationVersion: 0,
				elevationNumber: "",
				savedElevationNumber: "",
				referenceLabel: "",
				savedReferenceLabel: "",
				erefId: null,
				errored: false,
			},
		])
	}

	const submitNewElevation = (row: RowDraft) => {
		if (row.elevationId !== null) return
		const trimmed = row.elevationNumber.trim()
		if (trimmed.length === 0) return

		createEcallElevation(
			{
				quotationId,
				ecallId: ecall.id,
				body: {
					ecall_version: ecallVersionRef.current,
					elevation_number: trimmed,
					reference_label: row.referenceLabel || undefined,
				},
			},
			{
				onSuccess: (response) => {
					const created = response?.data
					if (!created) return
					const nextEcallVersion = bumpEcallVersion()
					replaceRow(row.key, {
						...row,
						key: `el-${created.id}`,
						elevationId: created.id,
						elevationVersion: created.version ?? 0,
						elevationNumber: created.elevation_number,
						savedElevationNumber: created.elevation_number,
						referenceLabel: created.reference_label ?? "",
						savedReferenceLabel: created.reference_label ?? "",
						erefId: created.eref_id,
						errored: false,
					})
					writeCache((cached) => appendEcallElevationToCache(cached, ecall.id, nextEcallVersion, created))
				},
				onError: handleApiError,
			},
		)
	}

	const patchElevation = (
		row: RowDraft,
		patch: { elevation_number?: string; reference_label?: string; eref_id?: number | null },
	) => {
		if (row.elevationId === null) return
		const elevationNumberToSend = patch.elevation_number ?? row.savedElevationNumber
		const erefIdToSend = patch.eref_id !== undefined ? patch.eref_id : row.erefId
		const erefVersionToSend = erefIdToSend !== null ? lookupErefVersion(erefIdToSend) : undefined
		updateEcallElevations(
			{
				quotationId,
				ecallId: ecall.id,
				body: {
					ecall_version: ecallVersionRef.current,
					elevations: [
						{
							version: row.elevationVersion,
							ecall_elevation_id: row.elevationId,
							elevation_number: elevationNumberToSend,
							eref_id: erefIdToSend,
							...(patch.reference_label !== undefined && { reference_label: patch.reference_label }),
							...(erefVersionToSend !== undefined && { eref_version: erefVersionToSend }),
						},
					],
				},
			},
			{
				onSuccess: (response) => {
					const data = response?.data
					const updated = data?.elevations?.find((el) => el.id === row.elevationId)
					if (!updated) return
					const nextEcallVersion = bumpEcallVersion()
					updateRow(row.key, {
						elevationVersion: updated.version ?? row.elevationVersion + 1,
						elevationNumber: updated.elevation_number,
						savedElevationNumber: updated.elevation_number,
						referenceLabel: updated.reference_label ?? "",
						savedReferenceLabel: updated.reference_label ?? "",
						erefId: updated.eref_id,
						errored: false,
					})
					writeCache((cached) => {
						const next = replaceEcallElevationInCache(cached, ecall.id, updated, nextEcallVersion)
						return applyDisconnectedErefsToCache(next, ecall.id, data?.disconnected_erefs)
					})
				},
				onError: handleApiError,
			},
		)
	}

	const handleElevationNumberBlur = (row: RowDraft) => {
		if (row.elevationId === null) {
			submitNewElevation(row)
			return
		}
		if (row.elevationNumber === row.savedElevationNumber) return
		patchElevation(row, { elevation_number: row.elevationNumber })
	}

	const handleReferenceLabelBlur = (row: RowDraft) => {
		if (row.elevationId === null) return
		if (row.referenceLabel === row.savedReferenceLabel) return
		patchElevation(row, { reference_label: row.referenceLabel })
	}

	const handleAutoConnect = (row: RowDraft) => {
		if (row.elevationId === null) return
		setPendingConnectRowKey(row.key)
		autoConnectEcallElevation(
			{
				quotationId,
				ecallId: ecall.id,
				ecallElevationId: row.elevationId,
				body: { ecall_version: ecallVersionRef.current },
			},
			{
				onSuccess: (response) => {
					setPendingConnectRowKey(null)
					const data = response?.data
					if (!data) return

					const { ecall_elevation, ecall: updatedEcall } = data
					if (ecall_elevation.eref_id !== null) {
						ecallVersionRef.current = updatedEcall.version
						updateRow(row.key, {
							elevationVersion: ecall_elevation.version,
							erefId: ecall_elevation.eref_id,
							errored: false,
						})
						writeCache((cached) => applyAutoConnectEcallElevationIntoCache(cached, data))
					} else {
						updateRow(row.key, { errored: true })
					}
				},
				onError: (e) => {
					setPendingConnectRowKey(null)
					updateRow(row.key, { errored: true })
					formatError(e)
				},
			},
		)
	}

	const performRemove = (row: RowDraft) => {
		if (row.elevationId === null) {
			removeRowLocal(row.key)
			return
		}
		const erefIdAtRemoval = row.erefId
		deleteEcallElevation(
			{ quotationId, ecallId: ecall.id, ecallElevationId: row.elevationId },
			{
				onSuccess: () => {
					const nextEcallVersion = bumpEcallVersion()
					removeRowLocal(row.key)
					writeCache((cached) => {
						const next = removeEcallElevationFromCache(cached, ecall.id, row.elevationId as number, nextEcallVersion)
						return erefIdAtRemoval !== null ? incrementErefVersionInCache(next, erefIdAtRemoval) : next
					})
				},
				onError: handleApiError,
			},
		)
	}

	const performDetach = (row: RowDraft) => {
		if (row.elevationId === null || row.erefId === null) return
		const detachedErefId = row.erefId
		updateEcallElevations(
			{
				quotationId,
				ecallId: ecall.id,
				body: {
					ecall_version: ecallVersionRef.current,
					elevations: [
						{
							version: row.elevationVersion,
							ecall_elevation_id: row.elevationId,
							elevation_number: row.savedElevationNumber,
							eref_id: null,
						},
					],
				},
			},
			{
				onSuccess: (response) => {
					const data = response?.data
					const updated = data?.elevations?.find((el) => el.id === row.elevationId)
					if (!updated) return
					const nextEcallVersion = bumpEcallVersion()
					const disconnectedErefs = data?.disconnected_erefs ?? []
					updateRow(row.key, {
						elevationVersion: updated.version ?? row.elevationVersion + 1,
						erefId: null,
						errored: false,
					})
					writeCache((cached) => {
						const next = replaceEcallElevationInCache(cached, ecall.id, updated, nextEcallVersion)
						const withDisconnected = applyDisconnectedErefsToCache(next, ecall.id, disconnectedErefs)
						// A detach always bumps the eref's version on the backend. Prefer the
						// authoritative version from `disconnected_erefs`, but fall back to a
						// +1 bump when the backend didn't include the just-detached eref there,
						// so its cached version never goes stale (avoids a later 409).
						return disconnectedErefs.some((eref) => eref.id === detachedErefId)
							? withDisconnected
							: incrementErefVersionInCache(withDisconnected, detachedErefId)
					})
				},
				onError: handleApiError,
			},
		)
	}

	const resolveRowState = (row: RowDraft): TElevationRowState => {
		if (row.errored) return "error"
		if (row.erefId !== null) return "connected"
		if (row.elevationNumber.trim().length > 0) return "filled"
		return "default"
	}

	const confirmRow = confirmAction ? rows.find((r) => r.key === confirmAction.rowKey) : undefined

	const handleConfirm = () => {
		if (!confirmAction || !confirmRow) return
		if (confirmAction.kind === "remove") {
			performRemove(confirmRow)
		} else {
			performDetach(confirmRow)
		}
		setConfirmAction(null)
	}

	const confirmElevation = confirmRow?.elevationNumber || "—"
	const confirmTitle = !confirmAction
		? ""
		: confirmAction.kind === "remove"
			? `Remove elevation number ${confirmElevation}?`
			: `Detach elevation ${confirmElevation}?`

	const confirmDescription =
		confirmAction?.kind === "remove"
			? confirmRow?.erefId != null
				? "If this number is connected to an elevation reference, the connection will also be removed."
				: "This action cannot be undone."
			: "Elevation reference will be detached from elevation callout. You can reconnect it later."

	return (
		<div className={style.sidebar} data-hotkey-sidebar tabIndex={-1}>
			<div className={style.header} data-hotkey-skip>
				<span className={style.headerTitle}>Elevation callout</span>
				<div className={style.headerActions}>
					<button type="button" className={style.headerCloseButton} onClick={onClose} aria-label="Close sidebar">
						<CloseIcon />
					</button>
				</div>
			</div>

			<div className={style.content}>
				<div className={style.details}>
					<div className={style.detailField}>
						<span className={style.detailLabel}>Room name</span>
						<input
							className={style.detailInputField}
							value={roomName}
							onChange={(e) => setRoomName(e.target.value)}
							onBlur={() => commitEcallField("room_name", roomName, savedRoomNameRef)}
						/>
					</div>
					<div className={style.detailField}>
						<span className={style.detailLabel}>Referencing page</span>
						<input
							className={style.detailInputField}
							value={pageName}
							onChange={(e) => setPageName(e.target.value)}
							onBlur={() => commitEcallField("page_name", pageName, savedPageNameRef)}
						/>
					</div>
				</div>

				<div className={style.divider} />

				<div className={style.elevationsSection}>
					<div className={style.sectionHeader}>
						<span className={style.sectionTitle}>Elevation references</span>
						<button
							type="button"
							className={style.addButton}
							onClick={handlePlus}
							disabled={addDisabled}
							aria-label="Add elevation reference"
						>
							<PlusGlyph disabled={addDisabled} />
						</button>
					</div>

					{rows.length > 0 && (
						<div className={style.rowsList}>
							{rows.map((row) => {
								const targetErefId = row.erefId
								const navigateHandler =
									targetErefId != null && onNavigateToEref ? () => onNavigateToEref(targetErefId) : undefined
								return (
									<ElevationReferenceRow
										key={row.key}
										elevationNumber={row.elevationNumber}
										connectedPageName={targetErefId !== null ? lookupErefPageName(targetErefId) : undefined}
										referenceLabel={row.referenceLabel}
										state={resolveRowState(row)}
										connectPending={pendingConnectRowKey === row.key}
										onElevationNumberChange={(next) => updateRow(row.key, { elevationNumber: next })}
										onReferenceLabelChange={(next) => updateRow(row.key, { referenceLabel: next })}
										onElevationNumberBlur={() => handleElevationNumberBlur(row)}
										onReferenceLabelBlur={() => handleReferenceLabelBlur(row)}
										onRemove={() => setConfirmAction({ kind: "remove", rowKey: row.key })}
										onConnect={() => handleAutoConnect(row)}
										onUnlink={() => setConfirmAction({ kind: "detach", rowKey: row.key })}
										onNavigateToEref={navigateHandler}
									/>
								)
							})}
						</div>
					)}
				</div>
			</div>

			<ConfirmModal
				isOpen={confirmAction !== null}
				onClose={() => setConfirmAction(null)}
				onConfirm={handleConfirm}
				title={confirmTitle}
				description={confirmDescription}
				confirmLabel={confirmAction?.kind === "remove" ? "Remove" : "Detach"}
			/>
		</div>
	)
}
